import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClickAwayListener, InputAdornment, Paper, Popper, TextField } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Search } from "@mui/icons-material";

import { Grey3, trakrefBlue } from "../../../../color_utils";
import { trakrefFontFamily } from "../../../../utils";
import useEmailAlert from "../context/useEmailAlert";
import { Input } from "../../../Common";
import ListPopper from "./ListPopper";

const CustomEmailAlertAutooCompleteField = ({ 
    emailAlertsError, 
    anchorEl, 
    setAnchorEl,
    searchEmailAlerts, 
    setSearchEmailAlerts,
    closeModal,
    searchRegion, 
    setSearchRegion,
    getSelectedEmailAlerts
}) => {
    const textRef = useRef();
    const {
        options,
        onFormUpdate,
        selectedEmailAlert
    } = useEmailAlert();

    const setSelectedEmailAlert = (value) => {
        onFormUpdate('selectedEmailAlert', value);
    }

    const mainOptions = useMemo(() => {
        return searchRegion
            ? options.filter(x => x.name.toLowerCase().includes(searchRegion.toLowerCase()))
            : options;
    }, [searchRegion]);

    const onParentRegionChanged = useCallback((e) => {
        const {
            target: { checked, value }
        } = e;

        if (checked) {
            const option = options.find(x => x.id === Number(value));
            const newArr = JSON.parse(JSON.stringify(selectedEmailAlert));
            const newArrIndex = newArr.findIndex(x => x.id === Number(value));
            if (newArrIndex > -1) {
                newArr[newArrIndex] = option;
                setSelectedEmailAlert(newArr);
            } else {
                setSelectedEmailAlert([...selectedEmailAlert, { ...option }])
            }
        }
        else {
            const filteredSelectedEmailAlert = selectedEmailAlert.filter(x => x.id !== Number(value));
            setSelectedEmailAlert([...filteredSelectedEmailAlert]);
        }
    }, [selectedEmailAlert]);

    const onEmailAlertChanged = useCallback((e) => {
        const {
            target: { checked, value }
        } = e;

        const parsedValue = JSON.parse(value);
        const newArr = JSON.parse(JSON.stringify(selectedEmailAlert));
        const instanceAssociationId = !('instanceAssociationId' in parsedValue) ? 0 : Number(parsedValue.instanceAssociationId); 

        const newArrIndex = instanceAssociationId === 0 && !!newArr.length ? newArr.findIndex(x => x.name === "without assigned region") : newArr.findIndex(x => x.id === instanceAssociationId);

        if (checked) {
            if (newArrIndex > -1) {
                const newArrValues = newArr[newArrIndex].emailAlerts.find(x => x.id === Number(parsedValue.id));
                if (!newArrValues) {
                    newArr[newArrIndex].emailAlerts.push(parsedValue)
                }
            }
            else {
                const newOptions = JSON.parse(JSON.stringify(options));
                const option = instanceAssociationId === 0 ? newOptions[instanceAssociationId] : newOptions.find(x => x.id === instanceAssociationId);
                option.emailAlerts = [parsedValue];
                newArr.push(option);
            }

            setSelectedEmailAlert(newArr);
            return;
        }

        const newArrValues = newArr[newArrIndex].emailAlerts.filter(x => x.id !== Number(parsedValue.id));
        newArr[newArrIndex].emailAlerts = newArrValues;

        setSelectedEmailAlert(newArr);
    }, [selectedEmailAlert]);

    useEffect(() => {
        setSearchEmailAlerts(getSelectedEmailAlerts());
    }, [])

    return (
        <ClickAwayListener onClickAway={closeModal}>
            <div style={{ width: '70%', marginBottom: '20px' }}>
                <Input
                    value={searchEmailAlerts}
                    onChange={(e) => setSearchEmailAlerts(e.target.value)}
                    label="Select Alerts"
                    fullWidth
                    onFocus={(event) => {
                        if (!anchorEl) {
                            setAnchorEl(event.currentTarget);
                            setSearchEmailAlerts("");
                        }
                    }}
                    inputRef={textRef}
                    endAdornment={(
                        <>
                            <img
                                src="images/ic_x.svg"
                                alt="x"
                                style={{ cursor: "pointer", marginRight: '5px', ...(!searchEmailAlerts && { visibility: 'hidden' }) }}
                                onClick={() => {
                                    setSelectedEmailAlert([]);
                                    setSearchEmailAlerts("");
                                }}
                            />
                            {
                                anchorEl === null ? (
                                    <KeyboardArrowDownIcon sx={{ color: trakrefBlue, cursor: "pointer" }} onClick={() => textRef.current.focus()} />
                                ) : (
                                    <KeyboardArrowUpIcon sx={{ color: trakrefBlue, cursor: "pointer" }} onClick={closeModal} />
                                )
                            }
                        </>
                    )}
                    error={emailAlertsError}
                />
                <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom" sx={{
                    zIndex: 9999,
                    width: 311,
                    marginLeft: `22px !important`
                }}>
                    <Paper sx={{
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            position: "absolute",
                            backgroundColor: "white",
                            zIndex: 99999,
                            width: "89%",
                            paddingTop: '10px'
                        }}>
                            <TextField
                                size="small"
                                placeholder="Filter by region"
                                fullWidth
                                value={searchRegion}
                                InputProps={{
                                    startAdornment: (
                                        < InputAdornment InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        searchRegion && <img src="images/ic_x.svg" alt="x" style={{ cursor: "pointer" }} onClick={() => setSearchRegion("")} />
                                    ),
                                    inputProps: {
                                        sx: {
                                            "&::placeholder": {
                                                color: Grey3,
                                                opacity: 1,
                                                fontSize: '14px',
                                            },
                                            fontFamily: trakrefFontFamily
                                        },
                                    },
                                }}
                                sx={{
                                    mt: 1,
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: Grey3,
                                        },
                                        pb: "2px"
                                    },
                                    '& legend': { display: 'none' },
                                    '& .MuiInputLabel-shrink': { opacity: 0, transition: "all 0.2s ease-in" }
                                }}
                                onChange={(e) => setSearchRegion(e.target.value)}
                                onMouseDown={(e) => {
                                    if (e.key !== "Escape") {
                                        e.stopPropagation();
                                    }
                                }}
                            />
                        </div>
                        <ListPopper
                            onParentRegionChanged={onParentRegionChanged}
                            onEmailAlertChanged={onEmailAlertChanged}
                            options={mainOptions}
                            origOptions={options}
                            searchEmailAlerts={searchEmailAlerts}
                        />
                    </Paper>
                </Popper>
            </div>
        </ClickAwayListener>
    )
}

export default CustomEmailAlertAutooCompleteField;